import { updateSessionById } from '@/pages/api/scribeAPI';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';

import { useIsOfflineConnectivity } from '@/hooks/desktop/useIsOfflineConnectivity';
import { MAX_QUERY_RETRIES } from '@/hooks/query/constants';
import { isSessionNotFoundError } from '@/lib/SessionRecordingProvider/errors';
import { logger } from '@/utils/logger/logger';
import { canPersistSessionToServer } from '@/utils/offline-recording/canPersistSessionToServer';
import {
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  type SessionResponse,
} from '@heidi/session/contracts';
import { notFoundSessionIds } from '@heidi/session/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Props = {
  sessionId: string;
  duration: number;
};
export const UPDATE_SESSION_DURATION = 'updateSessionDuration';
export const UPDATE_SESSION_DURATION_DEMO = 'updateSessionDurationDemo';

export function useMutateUpdateSessionDuration() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const isOffline = useIsOfflineConnectivity();
  const getSessionKey = GET_SESSION_BY_ID;

  return useMutation({
    retry: (failureCount, error) =>
      !isSessionNotFoundError(error) && failureCount < MAX_QUERY_RETRIES,
    mutationKey: [UPDATE_SESSION_DURATION],
    // Serialize all duration writes: a stop fires the click-time PATCH and the
    // authoritative upload-flush PATCH; without a scope React Query runs them
    // concurrently and a late-landing (retried) stale/lower value could clobber
    // the max-floored one on the server and in onSuccess's cache write. The
    // flush PATCH is always initiated last, so serialized = it settles last.
    scope: { id: UPDATE_SESSION_DURATION },
    mutationFn: async ({ sessionId, duration }: Props) => {
      // Gate the SERVER write only — the optimistic onMutate cache write still
      // runs, and it is what the header timer reads.
      //
      // An offline-local consult's id is client-minted and the server has no
      // record of it, so this PATCH 404s on every stop and raises a
      // destructive "Backend Error" toast over a recording that was captured
      // perfectly well. Nothing is lost by skipping it: Rust owns that
      // consult's `recorded_duration_ms`, and `duration` is one of the four
      // fields the sync engine's session PATCH carries once the server record
      // exists, so the durable write happens there rather than here.
      const session = queryClient.getQueryData<QuerySessionResponse>([
        getSessionKey,
        { sessionId },
      ]);
      if (!canPersistSessionToServer(session, isOffline)) {
        return;
      }
      // Send only `duration` — PATCH semantics leave omitted fields untouched
      // (the Chronicle stop flow already patches without `clinician_notes`).
      // Echoing cached notes here both risked clobbering newer server notes
      // and transmitted PHI a duration write never needed (APP-7623 review).
      return await updateSessionById(sessionId, {
        duration: duration,
      });
    },
    onMutate: ({ sessionId, duration }) => {
      // Optimistic seed for reads that arrive before onSuccess reconciles.
      queryClient.setQueryData<SessionResponse>(
        [getSessionKey, { sessionId: sessionId }],
        (oldData) => (oldData ? { ...oldData, duration: duration } : undefined)
      );
    },
    onSuccess: (result) => {
      // Destructured defensively, not eagerly: the guard above resolves with
      // `undefined` when the write was skipped, and `({ data }) => …` throws on
      // that before the null check below can run.
      const data = result?.data;
      // we called updateSessionById with an empty sessionId, so it will return null
      if (!data) return;

      queryClient.setQueryData<SessionResponse>(
        [getSessionKey, { sessionId: data.session_id }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          const newData: SessionResponse = {
            ...oldData,
            duration: data.duration,
          };
          return newData;
        }
      );
    },
    onError: (error, variables) => {
      if (isSessionNotFoundError(error)) {
        // Session was removed server-side; SessionRecordingProvider handles UI feedback.
        // The circuit breaker confirms that path has already fired; log anyway if it hasn't.
        if (!notFoundSessionIds.has(variables.sessionId)) {
          logger.warn(
            'Session not found when updating duration, session may have been deleted',
            { sessionId: variables.sessionId }
          );
        }
        return;
      }

      logger.error('Error:', error);
      toast({
        tone: 'destructive',
        title: t('toast.backendError'),
        description: error.message,
      });
    },
  });
}
